import { useTranslation } from '@unifyapps/i18n/client';
import { getIconFromRegistry } from '@unifyapps/icons/utils/getIconFromRegistry';
import { useMarkAsRead } from '@unifyapps/network/generated/notification-rest-api/notification-rest-api';
import { Box } from '@unifyapps/ui/components/Box';
import { Button } from '@unifyapps/ui/components/Button';
import type { FeaturedIconColors, FeaturedIconSizes } from '@unifyapps/ui/components/FeaturedIcon';
import { FeaturedIcon } from '@unifyapps/ui/components/FeaturedIcon';
import { Stack } from '@unifyapps/ui/components/Stack';
import type {
  TypographyColors,
  TypographyVariants,
  TypographyWeight,
} from '@unifyapps/ui/components/Typography';
import { Typography } from '@unifyapps/ui/components/Typography';
import { clsx } from 'clsx';
import { useCallback, useMemo } from 'react';
import { formatTime } from '../../../utils/time';
import { NotificationActionProvider } from '../providers/NotificationActionProvider';
import type { NotificationCategory, Notification as NotificationType } from '../types';

function TimeInterval({ createdTime }: { createdTime: number | undefined }) {
  const { t } = useTranslation(['common']);

  if (!createdTime) {
    return null;
  }

  const millisFromNow = Date.now() - createdTime;

  return (
    <Typography color="text-quaternary" variant="text-xxs" weight="regular">
      {millisFromNow > 60 * 1000
        ? t('common:TimeAgo', { time: formatTime(millisFromNow, 'minutes', 'comfortable', 1) })
        : t('common:JustNow')}
    </Typography>
  );
}

export function NotificationContent({
  category,
  content,
  createdTime,
  className,
  typography,
  icon,
}: {
  category: NotificationCategory | undefined;
  content: string;
  createdTime?: number;
  className?: string;
  typography?: {
    variant?: TypographyVariants;
    weight?: TypographyWeight;
    color?: TypographyColors;
  };
  icon?: {
    size?: FeaturedIconSizes;
    className?: string;
  };
}) {
  const html = useMemo(() => ({ __html: content }), [content]);

  const { name } = category?.icon ?? { name: 'InfoCircle' };

  const iconColor = category?.icon?.color ?? category?.color ?? 'gray';
  const NotificationIndicatorIcon = getIconFromRegistry(name);

  return (
    <Stack className={clsx('gap-md', className)} direction="row">
      {NotificationIndicatorIcon ? (
        <FeaturedIcon
          className="!rounded-full"
          color={iconColor as FeaturedIconColors}
          Icon={NotificationIndicatorIcon}
          iconClassName={icon?.className ?? 'size-3'}
          size={icon?.size ?? 'xs'}
          variant="notifications"
        />
      ) : null}
      <Stack className="gap-xxs" direction="column">
        <Typography
          color={typography?.color ?? 'text-secondary'}
          // biome-ignore lint/security/noDangerouslySetInnerHtml: because the configuration comes from automation and admin team makes it in html so it is not a user input but rather built by admin team
          dangerouslySetInnerHTML={html}
          variant={typography?.variant ?? 'text-xs'}
          weight={typography?.weight ?? 'regular'}
        />
        <TimeInterval createdTime={createdTime} />
      </Stack>
    </Stack>
  );
}

function NotificationItem({
  content,
  createdTime,
  category,
  className,
  read,
  id,
  onClick,
  onMarkAsRead,
  receiverType,
  ctaName,
}: {
  content: string;
  createdTime: number | undefined;
  category: NotificationCategory | undefined;
  className?: string;
  read?: boolean;
  id: string;
  onClick: (() => void) | undefined;
  onMarkAsRead?: (id: string) => void;
  receiverType?: string;
  ctaName?: string;
}) {
  const { mutate: markAsRead } = useMarkAsRead({
    mutation: {
      onSuccess: () => onMarkAsRead?.(id),
    },
  });

  const handleClick = useCallback(() => {
    markAsRead({ params: { notificationId: id, receiverType: receiverType ?? 'USER' } });
    onClick?.();
  }, [markAsRead, id, receiverType, onClick]);

  return (
    <Stack
      className={clsx(
        {
          'hover:bg-primary_hover cursor-pointer': !ctaName && Boolean(onClick),
        },
        className,
      )}
      direction="column"
      onClick={ctaName ? undefined : handleClick}
    >
      <Stack className="gap-sm justify-between" direction="row">
        <NotificationContent category={category} content={content} createdTime={createdTime} />
        {read ? null : <Box className="bg-brand-solid h-md w-md mt-0.5 shrink-0 rounded-full" />}
      </Stack>
      {ctaName ? (
        <Button
          className="w-fit mt-md ms-4xl"
          color="neutral"
          component="button"
          onClick={handleClick}
          size="xs"
          variant="solid"
        >
          {ctaName}
        </Button>
      ) : null}
    </Stack>
  );
}

function NotificationItemWrapper({
  notification,
  className,
  onMarkAsRead,
}: {
  notification: NotificationType;
  className?: string;
  onMarkAsRead?: (id: string) => void;
}) {
  if (!notification.content || !notification.id) {
    return null;
  }

  return (
    <NotificationActionProvider cta={notification.cta}>
      {({ onClick }: { onClick: (() => void) | undefined }) => (
        <NotificationItem
          category={notification.category}
          className={className}
          //@ts-expect-error -- checked above
          content={notification.content}
          createdTime={notification.createdTime}
          ctaName={notification.cta?.name as string | undefined}
          //@ts-expect-error -- checked above
          id={notification.id}
          onClick={onClick}
          onMarkAsRead={onMarkAsRead}
          read={notification.read}
          receiverType={notification.receiverType}
        />
      )}
    </NotificationActionProvider>
  );
}

export { NotificationItemWrapper as NotificationItem };
